import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  getLensCups, getLensCupSchema, getScenario,
  createScenario, updateScenario, startRun, getRun, deleteRun,
  getDataSources, getDataSource,
} from '../api/lens'
import type {
  LensScenario, LensGoalType, LensFeatureTrait, LensFeatureRelation,
  LensTargetConfig, LensCup, LensCupSchema, LensRun,
  LensDataSource,
} from '../types'
import { ChevronLeft, Play, Save, ExternalLink, Telescope, Database, Trash2 } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'

type SourceMode = 'cup' | 'datasource'

function _datasourceSchema(ds: LensDataSource): LensCupSchema {
  return {
    traits: ds.columns.map((col) => ({
      cup_trait_id: col.name,
      trait_id: 'datasource_col',
      title: col.name,
      type: col.type,
      is_identifier: false,
    })),
    relations: [],
  }
}

const GOAL_TYPES: { value: LensGoalType; label: string; desc: string }[] = [
  { value: 'classification', label: 'Classification', desc: 'Predict which category a record will fall into' },
  { value: 'segmentation',   label: 'Segmentation',   desc: 'Group records into natural clusters' },
  { value: 'association',    label: 'Association',     desc: 'Find items that co-occur (cross-sell, up-sell)' },
  { value: 'forecast',       label: 'Forecast',        desc: 'Predict a numeric value over time (demand, quantity)' },
  { value: 'anomaly',        label: 'Anomaly',         desc: 'Flag unusual or outlier records' },
]

const GOAL_COLORS: Record<LensGoalType, string> = {
  classification: 'border-blue-400 bg-blue-50',
  segmentation:   'border-violet-400 bg-violet-50',
  association:    'border-amber-400 bg-amber-50',
  forecast:       'border-emerald-400 bg-emerald-50',
  anomaly:        'border-red-400 bg-red-50',
}

const GOAL_BADGE: Record<LensGoalType, string> = {
  classification: 'bg-blue-50 text-blue-700',
  segmentation:   'bg-violet-50 text-violet-700',
  association:    'bg-amber-50 text-amber-700',
  forecast:       'bg-emerald-50 text-emerald-700',
  anomaly:        'bg-red-50 text-red-700',
}

const FORECAST_MODELS = [
  { value: 'gradient_boosting',      label: 'Gradient Boosting',      desc: 'Robust tree-based model, handles seasonality and outliers well' },
  { value: 'lightgbm',               label: 'LightGBM',               desc: 'Faster gradient boosting — better for larger datasets' },
  { value: 'exponential_smoothing',  label: 'Exponential Smoothing',   desc: 'Holt-Winters — best for clean seasonal data with limited records' },
]

const TYPE_BADGE: Record<string, string> = {
  numeric:     'bg-blue-50 text-blue-600',
  date:        'bg-emerald-50 text-emerald-600',
  categorical: 'bg-amber-50 text-amber-700',
  boolean:     'bg-violet-50 text-violet-600',
  text:        'bg-paper-sunken text-ink-dim',
}

const STATUS_DOT: Record<string, string> = {
  queued:  'bg-ink-dim/40',
  running: 'bg-amber-400 animate-pulse',
  done:    'bg-emerald-500',
  failed:  'bg-red-400',
}

const STATUS_TEXT: Record<string, string> = {
  queued:  'text-ink-dim',
  running: 'text-amber-600',
  done:    'text-emerald-600',
  failed:  'text-red-500',
}

type WizardStep = 1 | 2 | 3 | 4 | 5
type PageTab = 'config' | 'runs'

export function LensScenarioDetail() {
  const { id } = useParams<{ id: string }>()
  const isNew = id === 'new'
  const navigate = useNavigate()

  const [pageTab, setPageTab] = useState<PageTab>('config')

  // Wizard state
  const [wizardStep, setWizardStep] = useState<WizardStep>(1)
  const [name, setName] = useState('')
  const [goalType, setGoalType] = useState<LensGoalType>('classification')
  const [selectedCup, setSelectedCup] = useState<LensCup | null>(null)
  const [schema, setSchema] = useState<LensCupSchema | null>(null)
  const [featureTraits, setFeatureTraits] = useState<LensFeatureTrait[]>([])
  const [featureRelations, setFeatureRelations] = useState<LensFeatureRelation[]>([])
  const [targetConfig, setTargetConfig] = useState<LensTargetConfig>({})
  const [hyperparams, setHyperparams] = useState<Record<string, unknown>>({})

  // DataSource state
  const [sourceMode, setSourceMode]               = useState<SourceMode>('cup')
  const [datasources, setDatasources]             = useState<LensDataSource[]>([])
  const [selectedDatasource, setSelectedDatasource] = useState<LensDataSource | null>(null)

  // Data
  const [cups, setCups] = useState<LensCup[]>([])
  const [scenario, setScenario] = useState<LensScenario | null>(null)
  const [runs, setRuns] = useState<LensRun[]>([])
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)
  const [pollingRunId, setPollingRunId] = useState<string | null>(null)

  useEffect(() => {
    getLensCups().then(setCups)
    getDataSources().then(setDatasources)
  }, [])

  useEffect(() => {
    if (isNew || !id) return
    getScenario(id).then((s) => {
      setScenario(s)
      setRuns(s.runs ?? [])
      setName(s.name)
      setGoalType(s.goal_type)
      setFeatureTraits(s.feature_traits)
      setFeatureRelations(s.feature_relations)
      setTargetConfig(s.target_config)
      setHyperparams(s.hyperparams)

      if (s.datasource_id) {
        setSourceMode('datasource')
        getDataSource(s.datasource_id).then((ds) => {
          setSelectedDatasource(ds)
          setSchema(_datasourceSchema(ds))
        })
      } else {
        setSelectedCup({ id: s.cup_id, title: s.cup_title })
        getLensCupSchema(s.cup_id).then(setSchema)
      }
    })
  }, [id, isNew])

  // Poll the active run
  useEffect(() => {
    if (!pollingRunId) return
    const interval = setInterval(async () => {
      const run = await getRun(pollingRunId)
      setRuns((prev) => prev.map((r) => r.id === run.id ? run : r))
      if (run.status === 'done' || run.status === 'failed') {
        setPollingRunId(null)
        setRunning(false)
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [pollingRunId])

  const handleCupSelect = async (cup: LensCup) => {
    setSelectedCup(cup)
    setSelectedDatasource(null)
    setFeatureTraits([])
    setFeatureRelations([])
    setTargetConfig({})
    setSchema(null)
    const s = await getLensCupSchema(cup.id)
    setSchema(s)
  }

  const handleDatasourceSelect = (ds: LensDataSource) => {
    setSelectedDatasource(ds)
    setSelectedCup({ id: ds.root_cup_id, title: ds.name })
    setFeatureTraits([])
    setFeatureRelations([])
    setTargetConfig({})
    setSchema(_datasourceSchema(ds))
  }

  const toggleTrait = (t: LensFeatureTrait) => {
    setFeatureTraits((prev) =>
      prev.find((x) => x.cup_trait_id === t.cup_trait_id)
        ? prev.filter((x) => x.cup_trait_id !== t.cup_trait_id)
        : [...prev, t]
    )
  }

  const toggleRelation = (r: LensFeatureRelation) => {
    setFeatureRelations((prev) =>
      prev.find((x) => x.id === r.id)
        ? prev.filter((x) => x.id !== r.id)
        : [...prev, r]
    )
  }

  const handleSave = async () => {
    if (!selectedCup) return
    setSaving(true)
    try {
      const payload = {
        name,
        cup_id: selectedCup.id,
        cup_title: selectedDatasource ? selectedDatasource.name : selectedCup.title,
        goal_type: goalType,
        feature_traits: featureTraits,
        feature_relations: featureRelations,
        target_config: targetConfig,
        filters: [],
        hyperparams,
        datasource_id: selectedDatasource?.id ?? null,
      }
      if (isNew) {
        const created = await createScenario(payload)
        navigate(`/lens/scenarios/${created.id}`, { replace: true })
      } else if (scenario) {
        await updateScenario(scenario.id, {
          name, feature_traits: featureTraits, feature_relations: featureRelations,
          target_config: targetConfig, hyperparams,
        })
      }
    } finally {
      setSaving(false)
    }
  }

  const handleRun = async () => {
    if (!scenario) return
    setRunning(true)
    const run = await startRun(scenario.id)
    navigate(`/lens/runs/${run.id}`)
  }

  const handleDeleteRun = async (runId: string) => {
    await deleteRun(runId)
    setRuns((prev) => prev.filter((r) => r.id !== runId))
  }

  const tc = targetConfig
  const canProceedStep1 = name.trim() && (sourceMode === 'cup' ? selectedCup : selectedDatasource)
  const canProceedStep3 = featureTraits.length > 0 || featureRelations.length > 0
  const canRun = scenario && !running && featureTraits.length > 0

  const forecastModel = (hyperparams.model as string) || 'gradient_boosting'
  const isGbmModel = forecastModel !== 'exponential_smoothing'

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Link to="/lens/scenarios" className="text-ink-dim hover:text-ink-dim transition-colors">
          <ChevronLeft size={18} />
        </Link>
        <Telescope size={15} className="text-accent" />
        <h1 className="text-xl font-semibold text-ink flex-1">
          {isNew ? 'New Scenario' : name}
        </h1>
        {!isNew && scenario && (
          <span className={`text-theme-xs px-2 py-0.5 rounded font-medium ${GOAL_BADGE[scenario.goal_type]}`}>
            {scenario.goal_type}
          </span>
        )}
        <div className="flex items-center gap-2">
          {!isNew && scenario && (
            <Button variant="primary" size="sm" icon={Play} loading={running} onClick={handleRun} disabled={!canRun}>
              Run
            </Button>
          )}
          <Button variant="secondary" size="sm" icon={Save} onClick={handleSave} disabled={saving || !canProceedStep1}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Page-level tabs */}
      {!isNew && (
        <div className="flex gap-0 border-b border-paper-border mb-5">
          {(['config', 'runs'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setPageTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors capitalize ${
                pageTab === tab
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'
              }`}
            >
              {tab === 'config' ? 'Configuration' : `Runs${runs.length ? ` (${runs.length})` : ''}`}
            </button>
          ))}
        </div>
      )}

      {/* ── Configuration tab ───────────────────────────────────────── */}
      {(isNew || pageTab === 'config') && (
        <div>
          {/* Wizard step tabs */}
          <div className="flex gap-1 border-b border-paper-border mb-4">
            {(['Basics', 'Goal', 'Features', 'Target', 'Tune'] as const).map((label, i) => {
              const s = (i + 1) as WizardStep
              return (
                <button
                  key={label}
                  onClick={() => setWizardStep(s)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    wizardStep === s
                      ? 'border-accent text-accent'
                      : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'
                  }`}
                >
                  {i + 1}. {label}
                </button>
              )
            })}
          </div>

          {/* Step 1: Basics */}
          {wizardStep === 1 && (
            <div className="space-y-5">
              <div>
                <label className="block text-theme-xs font-medium text-ink-dim mb-1">Scenario name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Article demand forecast"
                  className="field w-full"
                />
              </div>
              <div>
                <label className="block text-theme-xs font-medium text-ink-dim mb-2">Data source</label>
                <div className="flex gap-1 mb-3 border border-paper-border rounded-lg p-0.5 w-fit">
                  {([
                    { val: 'cup', label: 'Direct cup', icon: null },
                    { val: 'datasource', label: 'Data source', icon: <Database size={11} /> },
                  ] as const).map(({ val, label, icon }) => (
                    <button
                      key={val}
                      onClick={() => {
                        setSourceMode(val)
                        setFeatureTraits([])
                        setFeatureRelations([])
                        setTargetConfig({})
                        setSchema(null)
                        setSelectedCup(null)
                        setSelectedDatasource(null)
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded text-theme-xs font-medium transition-colors ${
                        sourceMode === val ? 'bg-accent text-white' : 'text-ink-dim hover:text-ink'
                      }`}
                    >
                      {icon}{label}
                    </button>
                  ))}
                </div>

                {sourceMode === 'cup' && (
                  <div className="grid grid-cols-2 gap-2">
                    {cups.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => handleCupSelect(c)}
                        className={`text-left px-3 py-2.5 rounded border text-sm transition-all ${
                          selectedCup?.id === c.id && !selectedDatasource
                            ? 'border-accent bg-accent-soft text-accent font-medium'
                            : 'border-paper-border text-ink hover:border-paper-border'
                        }`}
                      >
                        {c.title}
                      </button>
                    ))}
                  </div>
                )}

                {sourceMode === 'datasource' && (
                  <div>
                    {datasources.length === 0 ? (
                      <p className="text-sm text-ink-dim">
                        No data sources yet. <a href="/lens/datasources/new" className="text-accent hover:underline">Create one</a> first.
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {datasources.map((ds) => (
                          <button
                            key={ds.id}
                            onClick={() => handleDatasourceSelect(ds)}
                            className={`w-full text-left px-3 py-2.5 rounded border text-sm transition-all ${
                              selectedDatasource?.id === ds.id
                                ? 'border-accent bg-accent-soft text-accent font-medium'
                                : 'border-paper-border text-ink hover:border-paper-border'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Database size={12} className="text-accent shrink-0" />
                              <span>{ds.name}</span>
                              {ds.cached_at ? (
                                <span className="text-[10px] text-emerald-600 font-medium ml-auto">{ds.n_rows.toLocaleString()} rows</span>
                              ) : (
                                <span className="text-[10px] text-amber-500 ml-auto">Not cached</span>
                              )}
                            </div>
                            <p className="text-[10.5px] text-ink-dim mt-0.5 ml-5">
                              {ds.columns.length} columns · {ds.hops.length} hop{ds.hops.length !== 1 ? 's' : ''} · {ds.output_mode}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex justify-end pt-2">
                <Button variant="primary" size="sm" onClick={() => setWizardStep(2)} disabled={!canProceedStep1}>
                  Next: Goal →
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Goal */}
          {wizardStep === 2 && (
            <div className="space-y-5">
              <div>
                <label className="block text-theme-xs font-medium text-ink-dim mb-2">Goal type</label>
                <div className="space-y-2">
                  {GOAL_TYPES.map((g) => (
                    <button
                      key={g.value}
                      onClick={() => setGoalType(g.value)}
                      className={`w-full text-left px-3 py-2.5 rounded border text-sm transition-all ${
                        goalType === g.value
                          ? `border-2 ${GOAL_COLORS[g.value]} font-medium`
                          : 'border-paper-border text-ink hover:border-paper-border'
                      }`}
                    >
                      <span className="font-medium">{g.label}</span>
                      <span className="text-ink-dim ml-2 font-normal">{g.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {goalType === 'forecast' && (
                <div>
                  <label className="block text-theme-xs font-medium text-ink-dim mb-2">Forecasting model</label>
                  <div className="space-y-2">
                    {FORECAST_MODELS.map((m) => (
                      <button
                        key={m.value}
                        onClick={() => setHyperparams({ ...hyperparams, model: m.value })}
                        className={`w-full text-left px-3 py-2.5 rounded border text-sm transition-all ${
                          forecastModel === m.value
                            ? 'border-2 border-emerald-400 bg-emerald-50 font-medium text-emerald-800'
                            : 'border-paper-border text-ink hover:border-paper-border'
                        }`}
                      >
                        <span className="font-medium">{m.label}</span>
                        <span className="text-ink-dim ml-2 font-normal">{m.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-2">
                <Button variant="secondary" size="sm" onClick={() => setWizardStep(1)}>← Back</Button>
                <Button variant="primary" size="sm" onClick={() => setWizardStep(3)}>Next: Features →</Button>
              </div>
            </div>
          )}

          {/* Step 3: Features */}
          {wizardStep === 3 && (
            <div className="space-y-5">
              {!schema ? (
                <p className="text-sm text-ink-dim">Select a cup first to see available traits.</p>
              ) : (
                <>
                  <div>
                    <label className="block text-theme-xs font-medium text-ink-dim mb-2">
                      Traits <span className="text-ink-dim font-normal">(select features)</span>
                    </label>
                    <div className="space-y-1">
                      {schema.traits.map((t) => {
                        const selected = featureTraits.some((x) => x.cup_trait_id === t.cup_trait_id)
                        return (
                          <label key={t.cup_trait_id} className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer text-sm transition-colors ${selected ? 'bg-accent-soft text-accent' : 'hover:bg-paper-sunken text-ink'}`}>
                            <input type="checkbox" checked={selected} onChange={() => toggleTrait(t)} className="rounded accent-indigo-600" />
                            <span className="flex-1">{t.title}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${TYPE_BADGE[t.type] ?? TYPE_BADGE.text}`}>{t.type}</span>
                            {t.is_identifier && <span className="text-[10px] text-ink-dim">id</span>}
                          </label>
                        )
                      })}
                    </div>
                  </div>
                  {schema.relations.length > 0 && (
                    <div>
                      <label className="block text-theme-xs font-medium text-ink-dim mb-2">
                        Relations <span className="text-ink-dim font-normal">(parent links)</span>
                      </label>
                      <div className="space-y-1">
                        {schema.relations.map((r) => {
                          const selected = featureRelations.some((x) => x.id === r.id)
                          return (
                            <label key={r.id} className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer text-sm transition-colors ${selected ? 'bg-accent-soft text-accent' : 'hover:bg-paper-sunken text-ink'}`}>
                              <input type="checkbox" checked={selected} onChange={() => toggleRelation(r)} className="rounded accent-indigo-600" />
                              <span className="flex-1">{r.label}</span>
                              <span className="text-[10px] text-ink-dim">relation</span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between pt-2">
                <Button variant="secondary" size="sm" onClick={() => setWizardStep(2)}>← Back</Button>
                <Button variant="primary" size="sm" onClick={() => setWizardStep(4)} disabled={!canProceedStep3}>Next: Target →</Button>
              </div>
            </div>
          )}

          {/* Step 4: Target */}
          {wizardStep === 4 && (
            <div className="space-y-5">
              {goalType === 'classification' && <ClassificationTarget schema={schema} tc={tc} onChange={setTargetConfig} />}
              {goalType === 'forecast'       && <ForecastTarget schema={schema} tc={tc} onChange={setTargetConfig} datasource={selectedDatasource} />}
              {goalType === 'association'    && <AssociationTarget schema={schema} tc={tc} onChange={setTargetConfig} />}
              {goalType === 'segmentation'   && <SegmentationTarget tc={tc} onChange={setTargetConfig} />}
              {goalType === 'anomaly'        && <AnomalyTarget tc={tc} onChange={setTargetConfig} />}
              <div className="flex justify-between pt-2">
                <Button variant="secondary" size="sm" onClick={() => setWizardStep(3)}>← Back</Button>
                <Button variant="primary" size="sm" onClick={() => setWizardStep(5)}>Next: Tune →</Button>
              </div>
            </div>
          )}

          {/* Step 5: Tune */}
          {wizardStep === 5 && (
            <div className="space-y-5">
              {goalType === 'classification' && (
                <div>
                  <label className="block text-theme-xs font-medium text-ink-dim mb-1">Number of trees <span className="text-ink-dim">(Random Forest)</span></label>
                  <input type="number" min={10} max={500} step={10}
                    value={(hyperparams.n_estimators as number) ?? 100}
                    onChange={(e) => setHyperparams({ ...hyperparams, n_estimators: parseInt(e.target.value) })}
                    className="field w-32"
                  />
                  <p className="text-theme-xs text-ink-dim mt-1">More trees = slower but more accurate. Default 100 works well.</p>
                </div>
              )}

              {goalType === 'segmentation' && (
                <div>
                  <label className="block text-theme-xs font-medium text-ink-dim mb-1">Number of segments</label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-sm text-ink-dim">
                      <input type="radio" checked={tc.n_clusters === 'auto' || tc.n_clusters === undefined} onChange={() => setTargetConfig({ ...tc, n_clusters: 'auto' })} />
                      Auto
                    </label>
                    <label className="flex items-center gap-1.5 text-sm text-ink-dim">
                      <input type="radio" checked={typeof tc.n_clusters === 'number'} onChange={() => setTargetConfig({ ...tc, n_clusters: 4 })} />
                      Manual:
                      <input type="number" min={2} max={20}
                        value={typeof tc.n_clusters === 'number' ? tc.n_clusters : 4}
                        onChange={(e) => setTargetConfig({ ...tc, n_clusters: parseInt(e.target.value) })}
                        className="field w-16 ml-1"
                        disabled={tc.n_clusters === 'auto' || tc.n_clusters === undefined}
                      />
                    </label>
                  </div>
                </div>
              )}

              {goalType === 'forecast' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-theme-xs font-medium text-ink-dim mb-1">
                      Lag features <span className="text-ink-dim font-normal">(past periods to use as input)</span>
                    </label>
                    <input type="number" min={1} max={12}
                      value={(hyperparams.n_lags as number) ?? 3}
                      onChange={(e) => setHyperparams({ ...hyperparams, n_lags: parseInt(e.target.value) || 3 })}
                      className="field w-24"
                    />
                    <p className="text-theme-xs text-ink-dim mt-1">Default 3. More lags can capture longer seasonal patterns but require more data.</p>
                  </div>

                  {isGbmModel && (
                    <>
                      <div>
                        <label className="flex items-center gap-2 text-theme-xs font-medium text-ink-dim cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={!!hyperparams.auto_tune}
                            onChange={(e) => setHyperparams({ ...hyperparams, auto_tune: e.target.checked })}
                          />
                          Auto-tune hyperparameters
                          <span className="text-ink-dim font-normal">(grid search — adds ~30 s per run)</span>
                        </label>
                      </div>

                      {!hyperparams.auto_tune && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-theme-xs font-medium text-ink-dim mb-1">Number of trees</label>
                            <input type="number" min={50} max={500} step={10}
                              value={(hyperparams.n_estimators as number) ?? 200}
                              onChange={(e) => setHyperparams({ ...hyperparams, n_estimators: parseInt(e.target.value) })}
                              className="field w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-theme-xs font-medium text-ink-dim mb-1">Max tree depth</label>
                            <input type="number" min={1} max={8}
                              value={(hyperparams.max_depth as number) ?? 3}
                              onChange={(e) => setHyperparams({ ...hyperparams, max_depth: parseInt(e.target.value) })}
                              className="field w-full"
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div>
                    <label className="flex items-center gap-2 text-theme-xs font-medium text-ink-dim cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!hyperparams.walk_forward_cv}
                        onChange={(e) => setHyperparams({ ...hyperparams, walk_forward_cv: e.target.checked })}
                      />
                      Walk-forward cross-validation
                      <span className="text-ink-dim font-normal">(adds mae_cv metric — more honest evaluation)</span>
                    </label>
                  </div>
                </div>
              )}

              {(goalType === 'anomaly' || goalType === 'association') && (
                <p className="text-sm text-ink-dim">No additional tuning parameters for this goal type.</p>
              )}

              <div className="flex justify-between pt-2">
                <Button variant="secondary" size="sm" onClick={() => setWizardStep(4)}>← Back</Button>
                <Button variant="primary" size="sm" icon={Save} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : isNew ? 'Save scenario' : 'Save changes'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Runs tab ────────────────────────────────────────────────── */}
      {!isNew && pageTab === 'runs' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-theme-xs text-ink-dim">{runs.length === 0 ? 'No runs yet.' : `${runs.length} run${runs.length !== 1 ? 's' : ''}`}</p>
            <Button variant="primary" size="sm" icon={Play} loading={running} onClick={handleRun} disabled={!canRun}>
              {running ? 'Running…' : 'Start new run'}
            </Button>
          </div>

          {runs.length === 0 ? (
            <div className="border border-dashed border-paper-border rounded-lg p-10 text-center">
              <Telescope size={28} className="text-ink-dim mx-auto mb-3" />
              <p className="text-sm text-ink-dim">Run the scenario to train the model and see results.</p>
              <Button variant="primary" size="sm" icon={Play} onClick={handleRun} disabled={!canRun} className="mt-3 mx-auto">
                Start first run
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {runs.map((r) => (
                <RunCard key={r.id} run={r} onNavigate={() => navigate(`/lens/runs/${r.id}`)} onDelete={() => handleDeleteRun(r.id)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function RunCard({ run, onNavigate, onDelete }: { run: LensRun; onNavigate: () => void; onDelete: () => void }) {
  const isClickable = run.status === 'done' || run.status === 'failed'
  const canDelete   = run.status !== 'running'
  return (
    <div
      onClick={isClickable ? onNavigate : undefined}
      className={`border border-paper-border rounded-lg px-4 py-3 flex items-center gap-4 transition-colors ${isClickable ? 'cursor-pointer hover:border-paper-border hover:bg-paper-sunken/60' : ''}`}
    >
      <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[run.status]}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium capitalize ${STATUS_TEXT[run.status]}`}>{run.status}</span>
          {run.applied_at && <span className="text-[10px] text-emerald-500 font-medium bg-emerald-50 px-1.5 py-0.5 rounded">Applied</span>}
        </div>
        <p className="text-theme-xs text-ink-dim mt-0.5">
          {run.completed_at
            ? new Date(run.completed_at).toLocaleString()
            : run.started_at
            ? `Started ${new Date(run.started_at).toLocaleString()}`
            : `Queued ${new Date(run.created_at).toLocaleString()}`}
        </p>
        {run.error && <p className="text-theme-xs text-red-500 mt-0.5 truncate">{run.error}</p>}
      </div>
      {isClickable && <ExternalLink size={13} className="text-ink-dim shrink-0" />}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete() }}
        disabled={!canDelete}
        className="p-1 text-ink-dim hover:text-red-500 transition-colors disabled:opacity-30 shrink-0"
        title="Delete run"
      >
        <Trash2 size={13} />
      </button>
    </div>
  )
}

// ── Target config sub-components ──────────────────────────────────────────────

function ClassificationTarget({ schema, tc, onChange }: { schema: LensCupSchema | null; tc: LensTargetConfig; onChange: (v: LensTargetConfig) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-theme-xs font-medium text-ink-dim mb-1">Target trait (what to predict)</label>
        <Select
          value={tc.trait_cup_trait_id ?? ''}
          onValueChange={(v) => { const t = schema?.traits.find((x) => x.cup_trait_id === v); onChange({ ...tc, trait_cup_trait_id: v, trait_title: t?.title }) }}
          placeholder="Select trait…"
        >
          {schema?.traits.map((t) => <SelectItem key={t.cup_trait_id} value={t.cup_trait_id}>{t.title} ({t.type})</SelectItem>)}
        </Select>
      </div>
      <div>
        <label className="block text-theme-xs font-medium text-ink-dim mb-1">Positive class <span className="text-ink-dim font-normal">(optional)</span></label>
        <input value={tc.positive_class ?? ''} onChange={(e) => onChange({ ...tc, positive_class: e.target.value })} placeholder="e.g. approved" className="field w-full" />
      </div>
    </div>
  )
}

function ForecastTarget({ schema, tc, onChange, datasource }: { schema: LensCupSchema | null; tc: LensTargetConfig; onChange: (v: LensTargetConfig) => void; datasource?: LensDataSource | null }) {
  const numericTraits = schema?.traits.filter((t) => t.type === 'numeric') ?? []
  const dateTraits    = schema?.traits.filter((t) => t.type === 'date') ?? []
  const allTraits     = schema?.traits ?? []
  const isDsMode      = !!datasource

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-theme-xs font-medium text-ink-dim mb-1">Numeric column to forecast</label>
        <Select value={tc.trait_cup_trait_id ?? ''} onValueChange={(v) => onChange({ ...tc, trait_cup_trait_id: v })} placeholder="Select…">
          {numericTraits.map((t) => <SelectItem key={t.cup_trait_id} value={t.cup_trait_id}>{t.title}</SelectItem>)}
        </Select>
      </div>
      <div>
        <label className="block text-theme-xs font-medium text-ink-dim mb-1">Time axis <span className="text-ink-dim font-normal">(date column, optional — falls back to created_at)</span></label>
        <Select
          value={tc.time_cup_trait_id || '__created_at__'}
          onValueChange={(v) => onChange({ ...tc, time_cup_trait_id: v === '__created_at__' ? undefined : v })}
        >
          <SelectItem value="__created_at__">created_at (automatic)</SelectItem>
          {dateTraits.map((t) => <SelectItem key={t.cup_trait_id} value={t.cup_trait_id}>{t.title}</SelectItem>)}
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-theme-xs font-medium text-ink-dim mb-1">Grain</label>
          <Select value={tc.grain ?? 'month'} onValueChange={(v) => onChange({ ...tc, grain: v as LensTargetConfig['grain'] })}>
            <SelectItem value="day">Day</SelectItem>
            <SelectItem value="week">Week</SelectItem>
            <SelectItem value="month">Month</SelectItem>
            <SelectItem value="quarter">Quarter</SelectItem>
          </Select>
        </div>
        <div>
          <label className="block text-theme-xs font-medium text-ink-dim mb-1">Horizon (periods ahead)</label>
          <input type="number" min={1} max={24} value={tc.horizon ?? 3} onChange={(e) => onChange({ ...tc, horizon: parseInt(e.target.value) || 3 })} onBlur={() => { if (!tc.horizon) onChange({ ...tc, horizon: 3 }) }} className="field w-full" />
        </div>
      </div>
      <div>
        <label className="block text-theme-xs font-medium text-ink-dim mb-1">
          Group by entity <span className="text-ink-dim font-normal">(optional — one forecast per entity)</span>
        </label>
        {isDsMode ? (
          <Select
            value={tc.group_by_column || '__none__'}
            onValueChange={(v) => onChange({ ...tc, group_by_column: v === '__none__' ? undefined : v, group_by_relation_label: undefined })}
          >
            <SelectItem value="__none__">Single global forecast</SelectItem>
            {allTraits.map((t) => <SelectItem key={t.cup_trait_id} value={t.cup_trait_id}>{t.title}</SelectItem>)}
          </Select>
        ) : (
          <Select
            value={tc.group_by_relation_label || '__none__'}
            onValueChange={(v) => onChange({ ...tc, group_by_relation_label: v === '__none__' ? undefined : v, group_by_column: undefined })}
          >
            <SelectItem value="__none__">Single global forecast</SelectItem>
            {schema?.relations.map((r) => <SelectItem key={r.id} value={r.label}>{r.label}</SelectItem>)}
          </Select>
        )}
        <p className="text-[10.5px] text-ink-dim mt-1">
          {isDsMode ? 'Select a column that identifies the entity (e.g. article reference).' : 'Select a relation label to group by (e.g. "article" groups per article).'}
        </p>
      </div>
    </div>
  )
}

function AssociationTarget({ schema, tc, onChange }: { schema: LensCupSchema | null; tc: LensTargetConfig; onChange: (v: LensTargetConfig) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-theme-xs font-medium text-ink-dim mb-1">Basket relation</label>
        <p className="text-theme-xs text-ink-dim mb-2">Which parent relation groups items into baskets? e.g. "order" groups articles.</p>
        <Select value={tc.basket_relation_label ?? ''} onValueChange={(v) => onChange({ ...tc, basket_relation_label: v })} placeholder="Select relation…">
          {schema?.relations.map((r) => <SelectItem key={r.id} value={r.label}>{r.label}</SelectItem>)}
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-theme-xs font-medium text-ink-dim mb-1">Min support</label>
          <div className="flex items-center gap-2">
            <input type="range" min={1} max={50} step={1} value={Math.round((tc.min_support ?? 0.05) * 100)} onChange={(e) => onChange({ ...tc, min_support: parseInt(e.target.value) / 100 })} className="flex-1" />
            <span className="text-theme-xs text-ink-dim w-10 text-right">{Math.round((tc.min_support ?? 0.05) * 100)}%</span>
          </div>
        </div>
        <div>
          <label className="block text-theme-xs font-medium text-ink-dim mb-1">Min confidence</label>
          <div className="flex items-center gap-2">
            <input type="range" min={10} max={100} step={5} value={Math.round((tc.min_confidence ?? 0.4) * 100)} onChange={(e) => onChange({ ...tc, min_confidence: parseInt(e.target.value) / 100 })} className="flex-1" />
            <span className="text-theme-xs text-ink-dim w-10 text-right">{Math.round((tc.min_confidence ?? 0.4) * 100)}%</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function SegmentationTarget({ tc, onChange }: { tc: LensTargetConfig; onChange: (v: LensTargetConfig) => void }) {
  return (
    <div>
      <label className="block text-theme-xs font-medium text-ink-dim mb-2">Number of segments</label>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-1.5 text-sm text-ink-dim">
          <input type="radio" checked={tc.n_clusters === 'auto' || tc.n_clusters === undefined} onChange={() => onChange({ ...tc, n_clusters: 'auto' })} />
          Auto (recommended)
        </label>
        <label className="flex items-center gap-1.5 text-sm text-ink-dim">
          <input type="radio" checked={typeof tc.n_clusters === 'number'} onChange={() => onChange({ ...tc, n_clusters: 4 })} />
          Manual:
          <input type="number" min={2} max={20} value={typeof tc.n_clusters === 'number' ? tc.n_clusters : 4} onChange={(e) => onChange({ ...tc, n_clusters: parseInt(e.target.value) })} className="field w-16 ml-1" disabled={tc.n_clusters === 'auto' || tc.n_clusters === undefined} />
        </label>
      </div>
    </div>
  )
}

function AnomalyTarget({ tc, onChange }: { tc: LensTargetConfig; onChange: (v: LensTargetConfig) => void }) {
  return (
    <div>
      <label className="block text-theme-xs font-medium text-ink-dim mb-1">Expected anomaly rate</label>
      <p className="text-theme-xs text-ink-dim mb-2">Approximate % of records you expect to be anomalous.</p>
      <div className="flex items-center gap-3">
        <input type="range" min={1} max={20} step={1} value={Math.round((tc.contamination ?? 0.05) * 100)} onChange={(e) => onChange({ ...tc, contamination: parseInt(e.target.value) / 100 })} className="w-48" />
        <span className="text-sm text-ink-dim font-medium">{Math.round((tc.contamination ?? 0.05) * 100)}%</span>
      </div>
    </div>
  )
}
